/**
 * Hub status API — GET /api/status (faircorp-api, same origin).
 * Path → serviceId mapping for Services page disabled state.
 */

/** Route path segment → service ID (must match Monitor/faircorp-api services[].id) */
export const SERVICE_IDS_FROM_PATH: Record<string, string> = {
  cloud: 'cloud',
  hosting: 'hosting',
  toolbox: 'toolbox',
  stockbot: 'stockbot',
  investor: 'investor',
  taxi: 'taxi',
  audit: 'audit',
};

export function pathToServiceId(path: string): string {
  const segment = path.startsWith('/') ? path.slice(1).split('/')[0] : path.split('/')[0];
  return segment && SERVICE_IDS_FROM_PATH[segment] ? SERVICE_IDS_FROM_PATH[segment] : segment || '';
}

export interface StatusBanner {
  id: string;
  message: string;
  severity: 'info' | 'warning' | 'error';
  visible: boolean;
  order: number;
}

export interface StatusService {
  id: string;
  enabled: boolean;
}

export interface OutageInfo {
  expectedReturn?: string;
  type?: string;
  servicesImpacted?: string[];
  dataLossRisks?: string;
  securityRisks?: string;
}

export interface StatusPayload {
  banners: StatusBanner[];
  services: StatusService[];
  outagePageActive: boolean;
  outageInfo: OutageInfo;
  publicHubStatusEnabled: boolean;
}

const STATUS_POLL_MS = 30_000;

let cached: StatusPayload | null = null;
let cachedAt = 0;

/** Clears the in-memory status cache (e.g. before a manual retry). */
export function clearStatusCache(): void {
  cached = null;
  cachedAt = 0;
}

export async function fetchStatus(): Promise<StatusPayload> {
  const now = Date.now();
  if (cached && now - cachedAt < STATUS_POLL_MS) {
    return cached;
  }
  const res = await fetch('/api/status');
  if (!res.ok) throw new Error('Failed to fetch status');
  const data = (await res.json()) as StatusPayload;
  cached = data;
  cachedAt = now;
  return data;
}

export function isServiceEnabled(services: StatusService[], serviceId: string): boolean {
  const s = services.find((x) => x.id === serviceId);
  return s?.enabled ?? true;
}
